/**
 * SSI Takeover Dialog
 *
 * Warning dialog shown when the Lead clinician chooses to take over editing
 * of the Semi-Structured Interview from the colleague who completed it
 * (SLaM / 3 Borough) — typically from the patient overview.
 *
 * Presents two paths:
 * (a) Proceed — take ownership of interview decisions/notes and AI-output review.
 *     The original record is saved as a read-only copy; the report continues
 *     to name the original clinician.
 * (b) Cancel — close and instead ask the colleague who completed the interview
 *     to make any needed changes themselves.
 *
 * Visual style matches SSIConflictAlert (Radix AlertDialog, amber warning,
 * left-aligned body copy, violet primary CTA).
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ExclamationIcon } from '@heroicons/react/outline';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {function} props.onConfirm - Called when the Lead confirms the takeover
 * @param {boolean} [props.isOverriding=false] - Loading state while CF runs
 * @param {string} [props.ssiClinicianName] - Name of the original SSI clinician
 */
export const SSIOverrideDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isOverriding = false,
  ssiClinicianName = 'your colleague',
}) => {
  const handleClose = () => {
    if (isOverriding) return;
    onClose?.();
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[650px] max-w-[90vw] max-h-[85vh] overflow-y-auto shadow-xl text-left">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <ExclamationIcon className="w-7 h-7 text-amber-600" />
            </div>
            <div className="flex-1">
              <AlertDialog.Title className="text-xl font-bold text-gray-900 text-left">
                Take over the Semi-Structured Interview?
              </AlertDialog.Title>
              <AlertDialog.Description className="text-sm text-gray-600 mt-1 text-left">
                You are about to take over editing from {ssiClinicianName} so you
                can update the Semi-Structured Interview yourself.
              </AlertDialog.Description>
            </div>
          </div>

          <div className="mb-4 text-left">
            <p className="text-gray-700 mb-3">
              Before continuing, decide whether you need to change the interview
              decisions yourself, or whether {ssiClinicianName} can make those
              changes.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 mb-6 text-left">
            <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
              <h4 className="text-sm font-semibold text-violet-800 mb-1">
                Option 1 — Take over editing
              </h4>
              <ul className="text-sm text-violet-700 space-y-1.5 list-disc list-inside">
                <li>
                  You will be able to edit symptom and day-to-day impact
                  decisions and notes in the Semi-Structured Interview.
                </li>
                <li>
                  You will become the reviewer for the Semi-Structured Interview
                  AI writing on the Documents page.
                </li>
                <li>
                  {ssiClinicianName} will only be able to view this module.
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-1">
                Option 2 — Ask your colleague
              </h4>
              <p className="text-sm text-gray-600">
                Close this dialog and ask {ssiClinicianName} to update the
                relevant decisions and re-approve any affected AI paragraphs
                themselves.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
            <h4 className="font-semibold text-amber-800 mb-2">What stays the same</h4>
            <ul className="text-sm text-amber-700 space-y-1.5 list-disc list-inside">
              <li>
                A read-only copy of the original interview answers is saved and
                remains available from the patient overview.
              </li>
              <li>
                The final patient report will continue to name {ssiClinicianName}
                as the clinician who completed the Semi-Structured Interview,
                with the original appointment date.
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={isOverriding}
              >
                Cancel — ask colleague instead
              </button>

              <button
                type="button"
                onClick={() => onConfirm?.()}
                className="px-4 py-2 text-sm font-semibold text-white bg-violet-700 rounded-md hover:bg-violet-800 transition-colors flex items-center gap-2"
                disabled={isOverriding}
              >
                {isOverriding ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Taking over...</span>
                  </>
                ) : (
                  <span>Take over editing</span>
                )}
              </button>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

export default SSIOverrideDialog;
